<template>
  <div
    v-element-visibility="(isVisible: boolean) => onSectionVisibility(isVisible, section, index)"
    class="relative flex flex-col gap-8 w-full overflow-auto"
    :data-section-id="section.id"
    :data-section-index="index"
    :style="{ ...style, ...getSectionThemeStyle(section, style) }"
  >
    <video
      v-if="isSectionLoaded(section, index) && section.theme?.background_video_url"
      :key="section.theme?.background_video_url"
      autoplay
      class="absolute top-0 left-0 z-1 w-full h-full object-cover"
      loop
      muted
      :src="section.theme?.background_video_url"
    />

    <SectionInfoProvider
      v-if="isSectionLoaded(section, index)"
      :section-idx="index"
      :section-list-id="id"
      :used-in-section-id="section.id"
    >
      <div
        class="canvas-section__content flex flex-col relative overflow-auto"
        :class="{
          'rounded-2 pa-4': !launchDarkly.enable_custom_display_overrides,
          'z-2': launchDarkly.disable_z_index_experiments || !!section.theme?.background_video_url,
        }"
      >
        <div
          v-for="(c, cIdx) in (sectionContentById[section.id]?.content ?? []) as ComponentNode[]"
          :key="c.id"
          v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, c)"
          class="component-wrapper relative"
          :data-section-component-id="c.id"
          :data-section-index="index"
          :style="getComponentStyle(c, section, cIdx, sectionContentById[section.id]?.content as ComponentNode[])"
        >
          <LinkableComponent :id="c.id" :used-in-section-id="section.id">
            <Component :is="componentMap[c.type]" v-bind="getComponentBindings(c)" :used-in-section-id="section.id" />
          </LinkableComponent>
        </div>
      </div>
    </SectionInfoProvider>
  </div>
</template>

<script setup lang="ts">
import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import SectionInfoProvider from '@canvas-builder/components/container/SectionInfoProvider/SectionInfoProvider.vue'
import type { CanvasRetrieve } from '@openapi'
import { computed, type ComputedRef, type CSSProperties, inject } from 'vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import { vElementVisibility } from '@vueuse/components'
import type { ComponentNode } from '@canvas-builder/types/canvas.ts'

defineProps<{
  section: CanvasRetrieve['sections'][number]
  index: number
  id: string
  sectionContentById: Record<string, { content: CanvasRetrieve['content'] }>
  getSectionThemeStyle: (
    section: CanvasRetrieve['sections'][number] & { theme?: CanvasRetrieve['theme'] },
    style?: CSSProperties,
  ) => Record<string, string>
  getComponentStyle: (
    c: ComponentNode,
    section: CanvasRetrieve['sections'][number],
    cIdx?: number,
    sectionContent?: ComponentNode[],
  ) => { marginBottom?: undefined } | { marginBottom: any }
  componentMap: Record<string, any>
  getComponentBindings: (c: ComponentNode) => ComponentNode
  onElementVisibility: (isVisible: boolean, c: ComponentNode) => void
  isSectionLoaded: (section: CanvasRetrieve['sections'][number], sectionIdx?: number) => boolean
  onSectionVisibility: (isVisible: boolean, section: CanvasRetrieve['sections'][number], sectionIdx?: number) => void
  style?: CSSProperties
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
</script>
